import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, HandCoins, CheckCircle2, Receipt } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const naira = (n) => `₦${(n || 0).toLocaleString()}`;

export default function Rewards() {
  const [pending, setPending] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        api.get("/admin/members", { params: { reward_status: "pending" } }),
        api.get("/admin/transactions"),
      ]);
      setPending(m.data); setTxns(t.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const disburse = async (id) => {
    setBusy(id);
    try { await api.post(`/admin/members/${id}/disburse`); toast.success("Reward disbursed (demo)"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const disburseAll = async () => {
    setBusy("batch");
    try { const { data } = await api.post("/admin/members/disburse-batch"); toast.success(`Disbursed ${data.disbursed} reward(s)`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl">Reward Management</h1>
          <p className="text-muted-foreground">₦2,000 onboarding support. <span className="font-semibold text-accent">Payouts are simulated (demo mode).</span></p>
        </div>
        {pending.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild><Button data-testid="batch-disburse" disabled={busy === "batch"} className="rounded-full bg-primary font-bold gap-2">{busy === "batch" ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandCoins className="w-4 h-4" />} Disburse All ({pending.length})</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Batch Disburse Rewards?</AlertDialogTitle><AlertDialogDescription>This will mark all {pending.length} pending members as disbursed and log transactions. (Simulated — no real transfer.)</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={disburseAll} className="bg-primary">Confirm</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-8">
        <div className="px-5 py-4 border-b flex items-center gap-2"><HandCoins className="w-5 h-5 text-amber-500" /><h3 className="font-heading font-bold">Pending Disbursement</h3></div>
        {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          : pending.length === 0 ? <div className="py-16 text-center text-muted-foreground"><CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />All rewards disbursed.</div>
          : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Member</th><th className="px-4 py-3">LGA</th><th className="px-4 py-3">Bank</th><th className="px-4 py-3">Account</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3"></th></tr></thead>
              <tbody>{pending.map((m) => (
                <tr key={m.member_id} data-testid={`pending-${m.member_id}`} className="border-t border-slate-50">
                  <td className="px-4 py-3"><div className="font-semibold">{m.full_name}</div><div className="text-xs font-mono text-muted-foreground">{m.member_id}</div></td>
                  <td className="px-4 py-3">{m.lga}</td><td className="px-4 py-3">{m.bank_name}</td><td className="px-4 py-3 font-mono text-xs">{m.account_number}</td>
                  <td className="px-4 py-3 font-semibold">{naira(m.reward_amount)}</td>
                  <td className="px-4 py-3"><Button data-testid={`disburse-${m.member_id}`} size="sm" disabled={busy === m.member_id} onClick={() => disburse(m.member_id)} className="rounded-full bg-primary font-bold gap-1">{busy === m.member_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Disburse</Button></td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center gap-2"><Receipt className="w-5 h-5 text-primary" /><h3 className="font-heading font-bold">Transaction Log</h3></div>
        {txns.length === 0 ? <div className="py-12 text-center text-muted-foreground text-sm">No transactions yet.</div> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Member ID</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th></tr></thead>
            <tbody>{txns.map((t) => (
              <tr key={t.id} className="border-t border-slate-50"><td className="px-4 py-3 font-mono text-xs">{t.member_id}</td><td className="px-4 py-3">{t.name}</td><td className="px-4 py-3 font-semibold">{naira(t.amount)}</td><td className="px-4 py-3"><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Success</Badge></td><td className="px-4 py-3 text-muted-foreground">{(t.created_at || "").slice(0, 10)}</td></tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
